import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
  theme
} from "antd";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { hasPermission } from "../../../commons/Permissions/PermissionsHook";
import { UserContext } from "../../../context/UserContext";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { deleteContract, getMeta, listContracts } from "./api";
import {
  BILLING_INTERVAL_LABELS,
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  STATUS_LABELS,
  formatMoney
} from "./constants";
import { DeadlineCell } from "./TermsSummary";
import UploadContractModal from "./UploadContractModal";
import VendorContractDrawer from "./VendorContractDrawer";
import "./vendorContracts.css";

const POLL_MS = 4000;
const MODULE = "Vendor-contracts";

const VendorContracts = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const user = React.useContext(UserContext);
  const can = action => hasPermission(user?.permissions, MODULE, action);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ searchText: "", category: undefined, status: "active" });
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, id: null });
  const pollTimer = useRef(null);

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await listContracts(filters);
      setData(res.data.data);
      setSummary(res.data.summary);
      // Keep refreshing while the assistant is still reading an upload.
      clearTimeout(pollTimer.current);
      if (res.data.data.some(item => item.extraction?.status === "processing")) {
        pollTimer.current = setTimeout(() => load(true), POLL_MS);
      }
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
    return () => clearTimeout(pollTimer.current);
  }, []);

  useEffect(() => {
    load();
  }, [filters]);

  const updateFilter = patch => setFilters(prev => ({ ...prev, ...patch }));

  const handleDelete = async id => {
    try {
      await deleteContract(id);
      Success(t("Contract deleted"));
      load();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const handleRead = (contract, ok) => {
    load(true);
    if (!ok) return;
    setUploadOpen(false);
    setDrawer({ open: true, id: contract._id });
  };

  const handleSaved = () => {
    setDrawer({ open: false, id: null });
    load(true);
  };

  const columns = [
    {
      title: t("Contract"),
      key: "title",
      mobile: "title",
      render: (_, item) => (
        <div className="vc-title-cell">
          <Typography.Link strong onClick={() => setDrawer({ open: true, id: item._id })}>
            {item.title || t("Contract")}
          </Typography.Link>
          <div className="vc-meta">
            {[item.providerName, item.subject].filter(Boolean).join(" · ")}
          </div>
          <Space size={[4, 4]} wrap>
            <Tag color={CATEGORY_COLORS[item.category]}>{t(CATEGORY_LABELS[item.category])}</Tag>
            {item.extraction?.status === "processing" && (
              <Tag icon={<LoadingOutlined />} color="processing">
                {t("Reading…")}
              </Tag>
            )}
            {item.extraction?.status === "failed" && (
              <Tooltip title={item.extraction.error}>
                <Tag color="error">{t("Could not be read")}</Tag>
              </Tooltip>
            )}
            {item.extraction?.status === "done" && !item.reviewed && (
              <Tag color="warning">{t("Please check")}</Tag>
            )}
          </Space>
        </div>
      )
    },
    {
      title: t("Cost"),
      key: "cost",
      width: 150,
      render: (_, item) =>
        item.price === null || item.price === undefined ? (
          "–"
        ) : (
          <>
            <div>{formatMoney(item.price, lng, item.currency)}</div>
            <Typography.Text type="secondary" className="vc-small">
              {t(BILLING_INTERVAL_LABELS[item.billingInterval])}
              {item.billingInterval !== "monthly" &&
                item.monthlyCost !== null &&
                ` · ${formatMoney(item.monthlyCost, lng, item.currency)}/${t("month")}`}
            </Typography.Text>
          </>
        )
    },
    {
      title: t("Cancellation deadline"),
      key: "deadline",
      width: 190,
      render: (_, item) =>
        item.extraction?.status === "processing" ? "…" : <DeadlineCell contract={item} lng={lng} />
    },
    {
      title: "",
      key: "actions",
      mobile: "actions",
      width: 96,
      align: "right",
      render: (_, item) => (
        <Space size={4}>
          <Tooltip title={t("Edit")}>
            <Button
              icon={<EditOutlined />}
              aria-label={t("Edit")}
              onClick={() => setDrawer({ open: true, id: item._id })}
            />
          </Tooltip>
          {can("Delete") && (
            <Popconfirm
              title={t("Delete this contract?")}
              description={t("The uploaded document is deleted from the Directory as well.")}
              okText={t("Delete")}
              okButtonProps={{ danger: true }}
              cancelText={t("Cancel")}
              onConfirm={() => handleDelete(item._id)}
            >
              <Button danger icon={<DeleteOutlined />} aria-label={t("Delete")} />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  // Phones get one card per contract, so the extra Card frame is left out there.
  const contractTable = (
    <ResponsiveTable
      rowKey="_id"
      loading={loading}
      columns={columns}
      dataSource={data}
      pagination={data.length > 50 ? { pageSize: 50 } : false}
      scroll={{ x: 640 }}
    />
  );

  const isEmpty = !loading && !data.length && !filters.searchText && !filters.category;

  return (
    <div className="vendor-contracts-page">
      {summary && (
        <Row gutter={[16, 16]} className="mb-3">
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("Per month")} value={formatMoney(summary.monthlyTotal, lng)} />
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic title={t("Per year")} value={formatMoney(summary.yearlyTotal, lng)} />
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic
                title={t("Cancellation deadlines soon")}
                value={summary.dueSoon}
                valueStyle={summary.dueSoon ? { color: token.colorError } : undefined}
              />
            </Card>
          </Col>
          <Col xs={12} lg={6}>
            <Card size="small">
              <Statistic
                title={t("To check")}
                value={summary.toReview}
                valueStyle={summary.toReview ? { color: token.colorWarning } : undefined}
              />
            </Card>
          </Col>
        </Row>
      )}

      {meta && !meta.configured && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t(
            "Reading contracts automatically is not set up on this server. You can still add contracts by hand."
          )}
        />
      )}

      <PageToolbar
        filters={
          <>
            <SearchBarComp handleSearch={value => updateFilter({ searchText: value })} />
            <Select
              allowClear
              className="vc-filter-select"
              placeholder={t("All categories")}
              value={filters.category}
              onChange={value => updateFilter({ category: value })}
              options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
                value,
                label: t(label)
              }))}
            />
            <Select
              allowClear
              className="vc-filter-select"
              placeholder={t("All")}
              value={filters.status}
              onChange={value => updateFilter({ status: value })}
              options={Object.entries(STATUS_LABELS).map(([value, label]) => ({
                value,
                label: t(label)
              }))}
            />
          </>
        }
        actions={
          can("Create") && (
            <>
              <Button icon={<PlusOutlined />} onClick={() => setDrawer({ open: true, id: null })}>
                {t("Add by hand")}
              </Button>
              {meta?.configured && (
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  onClick={() => setUploadOpen(true)}
                >
                  {t("Upload contract")}
                </Button>
              )}
            </>
          )
        }
      />

      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>
                  {t("All your contracts and their deadlines in one place")}
                </Typography.Title>
                <Typography.Paragraph type="secondary" className="vc-empty-text">
                  {t(
                    "Upload a phone, leasing, insurance or any other contract as a PDF or photo. The assistant reads the price, the term and the notice period, and the list shows you when to cancel."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {can("Create") && meta?.configured && (
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={() => setUploadOpen(true)}
              >
                {t("Upload contract")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : isMobile ? (
        contractTable
      ) : (
        <Card styles={{ body: { padding: 0 } }}>{contractTable}</Card>
      )}

      <UploadContractModal
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          load(true);
        }}
        onRead={handleRead}
      />
      <VendorContractDrawer
        open={drawer.open}
        contractId={drawer.id}
        onClose={() => setDrawer({ open: false, id: null })}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default VendorContracts;
